import { AlertTriangle, CheckCircle2, Clock3 } from "lucide-react";

import { Badge } from "../ui";

type DisputeCategory = "wrong_result" | "cheating" | "abandonment" | "technical" | "other";
type DisputeResolution = "upheld" | "overturned" | "cancelled";
type DisputeStatus = "open" | "under_review" | "resolved";

type MatchDisputeSummary = {
  id: string;
  reporterId: string;
  reporterUsername: string | null;
  category: DisputeCategory;
  status: DisputeStatus;
  resolution: DisputeResolution | null;
  adminNotes: string | null;
  createdAt: Date | string;
  resolvedAt: Date | string | null;
  withdrawnAt: Date | string | null;
};

type DisputeStatusBannerProps = {
  currentUserId: string;
  dispute: MatchDisputeSummary;
};

const categoryLabel: Record<DisputeCategory, string> = {
  wrong_result: "Wrong Result",
  cheating: "Cheating",
  abandonment: "Abandonment",
  technical: "Technical",
  other: "Other",
};

const resolutionLabel: Record<DisputeResolution, string> = {
  upheld: "Upheld",
  overturned: "Overturned",
  cancelled: "Cancelled",
};

export function DisputeStatusBanner({ currentUserId, dispute }: DisputeStatusBannerProps) {
  const isReporter = dispute.reporterId === currentUserId;
  const filedAt = toDate(dispute.createdAt);
  const resolvedAt = toDate(dispute.resolvedAt);
  const filedAtText = filedAt ? formatDateTime(filedAt) : "recently";

  return (
    <section className="rounded-xl border border-surface-700/40 bg-surface-800/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <StatusIcon status={dispute.status} />
            <h3 className="font-display text-sm font-bold uppercase tracking-wide text-surface-100">
              Dispute Status
            </h3>
            <Badge color={statusColor(dispute.status)} variant="subtle">
              {statusLabel(dispute.status)}
            </Badge>
            {dispute.withdrawnAt ? (
              <Badge color="yellow" variant="subtle">
                Withdrawn
              </Badge>
            ) : dispute.resolution ? (
              <Badge color={resolutionColor(dispute.resolution)} variant="subtle">
                {resolutionLabel[dispute.resolution]}
              </Badge>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-surface-300">
            {categoryLabel[dispute.category]} dispute filed {filedAtText}
            {dispute.reporterUsername ? ` by ${dispute.reporterUsername}` : ""}.
          </p>
          <p className="mt-1 text-xs text-surface-500">
            {isReporter
              ? "You filed this dispute. Moderators will update this status as they review it."
              : "A participant filed this dispute. Status updates will appear here."}
          </p>
        </div>
        <span className="text-xs text-surface-500">#{dispute.id.slice(-8)}</span>
      </div>

      {dispute.status === "resolved" ? (
        <div className="mt-3 rounded-lg border border-surface-700/60 bg-surface-875 p-3">
          <p className="text-sm text-surface-200">
            {resolvedSummary(dispute.resolution, dispute.withdrawnAt)}
            {resolvedAt ? ` Resolved ${formatDateTime(resolvedAt)}.` : ""}
          </p>
          {dispute.adminNotes ? (
            <p className="mt-2 text-sm text-surface-400">Admin notes: {dispute.adminNotes}</p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function StatusIcon({ status }: { status: DisputeStatus }) {
  if (status === "open") {
    return <AlertTriangle className="h-4 w-4 text-yellow-400" />;
  }
  if (status === "under_review") {
    return <Clock3 className="h-4 w-4 text-blue-400" />;
  }
  return <CheckCircle2 className="h-4 w-4 text-green-400" />;
}

function statusColor(status: DisputeStatus): "yellow" | "blue" | "green" {
  if (status === "open") return "yellow";
  if (status === "under_review") return "blue";
  return "green";
}

function statusLabel(status: DisputeStatus): string {
  if (status === "under_review") return "Under Review";
  if (status === "resolved") return "Resolved";
  return "Open";
}

function resolutionColor(resolution: DisputeResolution): "green" | "yellow" | "red" {
  if (resolution === "upheld") return "green";
  if (resolution === "overturned") return "yellow";
  return "red";
}

function resolvedSummary(
  resolution: DisputeResolution | null,
  withdrawnAt: Date | string | null,
): string {
  if (withdrawnAt) {
    return "The reporter withdrew this dispute. No changes were made to the match result.";
  }
  if (resolution === "upheld") {
    return "Result was upheld and no match correction was applied.";
  }
  if (resolution === "overturned") {
    return "Result was overturned and ratings were corrected.";
  }
  if (resolution === "cancelled") {
    return "Match was cancelled and ratings were voided.";
  }
  return "Dispute has been resolved.";
}

function toDate(value: Date | string | null): Date | null {
  if (!value) return null;
  return value instanceof Date ? value : new Date(value);
}

function formatDateTime(value: Date): string {
  if (Number.isNaN(value.getTime())) {
    return "recently";
  }
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(value);
}
